import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Centralized service for managing HTTP request cancellation.
 * Provides cancellation subjects based on whether requests should be cancelled on route navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class RequestCancellationService {
  /** Single router cancellation subject shared across all cancellable requests */
  private routeCancel$ = new Subject<void>();

  constructor(private router: Router) {
    // Single router subscription for the entire application
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.routeCancel$.next();
      });
  }

  /**
   * Gets a cancellation subject for a request.
   *
   * @param shouldCancel - Whether this request should be cancelled on route navigation
   * @param customCancelSubject - Optional custom cancellation subject (takes priority)
   * @returns Cancellation subject to use with takeUntil(), or undefined if cancellation is disabled
   *
   * @example
   * // Request that cancels on navigation (default)
   * const cancel$ = this.cancellationService.getCancellationSubject(true);
   * this.http.get(url).pipe(takeUntil(cancel$)).subscribe(...);
   *
   * @example
   * // Request that doesn't cancel on navigation
   * const cancel$ = this.cancellationService.getCancellationSubject(false);
   * // cancel$ is undefined, so takeUntil won't be applied
   *
   * @example
   * // Request with custom cancellation control
   * const customCancel$ = new Subject<void>();
   * const cancel$ = this.cancellationService.getCancellationSubject(true, customCancel$);
   * // Uses customCancel$ instead of route-based cancellation
   */
  getCancellationSubject(
    shouldCancel: boolean,
    customCancelSubject?: Subject<any> | null
  ): Subject<any> | undefined {
    if (!shouldCancel) {
      return undefined;
    }
    return customCancelSubject || this.routeCancel$;
  }

  /**
   * Gets the shared route cancellation subject directly.
   * Used by services that need to subscribe to route changes for manual handling (e.g., XHR).
   *
   * @param shouldCancel - Whether cancellation is enabled
   * @returns The route cancellation subject, or undefined if disabled
   */
  getRouteCancellationSubject(
    shouldCancel: boolean
  ): Subject<void> | undefined {
    return shouldCancel ? this.routeCancel$ : undefined;
  }
}
